import { BlcToastContainer } from '@Backlot-Cars/archie';
import { BlcFavicon } from 'components/BlcFavicon';
import NavigationWrapper from 'components/NavigationWrapper';
import StockwaveScript from 'components/Stockwave/StockwaveScript';
import { getErrorBoundary } from 'helpers/errorNotifier';
import AudioEyeScript from 'helpers/integrations/AudioEye';
import SegmentSnippet from 'helpers/integrations/SegmentSnippet/Snippet';
import { appWithTranslation } from 'next-i18next';
import App from 'next/app';
import { Provider } from 'react-redux';
import { wrapper } from 'store/';
import { useLoggedUserSelector } from 'store/slices/loggedUsers/selector';
import AppGlobalStyles from '../globalStyles';
import Error from './_error';
import initializeBugsnagClient from '/lib/bugsnag';
import OrttoSnippet from '../helpers/integrations/Ortto';
import { buildOrttoTrackingPayload } from '../helpers/integrations/Ortto/payload';
import { trackLoggedInOrttoUser } from '../helpers/integrations/Ortto/trackLoggedInUser';
import { Fragment, useEffect } from 'react';
import Bugsnag from '@bugsnag/js';
import { isFeatureEnabled, publicEnvConfig } from '../helpers/env';
import { StyleSheetManager } from 'styled-components';
import { shouldForwardProp } from '../helpers/styled-components';
import OktaService from 'helpers/okta';
import FeatureFlagsContext from 'contexts/featureFlags/featureFlagsContext';
import { useRackMiniProfiler } from 'helpers/integrations/RackMiniProfiler';

import { ThemeProvider as IgniteThemeProvider, AlertNotificationToaster } from '@kar-dmp/ignite';
import '@kar-dmp/ignite/styles.css';
import '@Backlot-Cars/archie/dist/index.css';
import '../tailwind.css';

initializeBugsnagClient();

const ErrorBoundary = getErrorBoundary();

const AppContent = ({ Component, pageProps }) => {
	const user = useLoggedUserSelector();
	const loggedUser = Boolean(user?.id);
	const Layout = Component.Layout ?? Fragment;
	const Content = Component.Layout?.Content ?? Fragment;

	useRackMiniProfiler(loggedUser);

	useEffect(() => {
		// Initialize Okta Service to start listening to tokenManager events
		OktaService.getInstance();
	}, []);

	useEffect(() => {
		if (isFeatureEnabled(publicEnvConfig.BUGSNAG_ENABLED) && user?.id) {
			Bugsnag.setUser(user.id?.toString(), user.email, user.dealership_name);
		}
	}, [user]);

	useEffect(() => {
		if (
			isFeatureEnabled(publicEnvConfig.ORTTO_ENABLED) &&
			user?.email &&
			user?.id &&
			typeof window !== 'undefined' &&
			window.ap3c
		) {
			const payload = buildOrttoTrackingPayload({
				email: user.email,
				userId: user.id,
			});

			trackLoggedInOrttoUser({
				ap3c: window.ap3c,
				trackingId: `${publicEnvConfig.NEXT_PUBLIC_ORTTO_TRACKING_ID}`,
				payload,
				disableWidgets: false,
			});
		}
	}, [user?.email, user?.id]);

	const page = (
		<Content>
			<Component {...pageProps} />
		</Content>
	);

	return (
		<FeatureFlagsContext.Provider value={pageProps?.featureFlags || {}}>
			<StyleSheetManager shouldForwardProp={shouldForwardProp}>
				<IgniteThemeProvider theme="omp" mode="light">
					<ErrorBoundary FallbackComponent={Error}>
						{isFeatureEnabled(publicEnvConfig.AUDIOEYE_ENABLED) && <AudioEyeScript />}
						{isFeatureEnabled(publicEnvConfig.STOCKWAVE_ENABLED) && <StockwaveScript />}
						{isFeatureEnabled(publicEnvConfig.SEGMENT_ENABLED) && <SegmentSnippet />}
						{isFeatureEnabled(publicEnvConfig.ORTTO_ENABLED) && <OrttoSnippet isLoggedIn={loggedUser} />}
						<AppGlobalStyles />
						<BlcFavicon />
						<Layout>
							{loggedUser ?
								<NavigationWrapper user={user}>{page}</NavigationWrapper>
							:	page}
						</Layout>
						<BlcToastContainer top={loggedUser ? 56 : undefined} />
						<AlertNotificationToaster
							visibleToasts={5}
							offset={loggedUser ? { top: 72 } : undefined}
							position="top-right"
						/>
					</ErrorBoundary>
				</IgniteThemeProvider>
			</StyleSheetManager>
		</FeatureFlagsContext.Provider>
	);
};

const MyApp = (appProps) => {
	const { store, props } = wrapper.useWrappedStore(appProps);

	return (
		<Provider store={store}>
			<AppContent {...props} />
		</Provider>
	);
};

MyApp.getInitialProps = async (appContext) => {
	const appProps = await App.getInitialProps(appContext);
	return { ...appProps };
};

export default appWithTranslation(MyApp);
